import React, { useEffect, useState } from 'react'
import { CircularProgress, Container, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { subjectReason, SubjectStatus, SubjectType } from 'constants/index'
import { Box } from '@mui/material'
import PageNotFound from 'components/PageNotFound'
import { fetchSubjectByToken, sendConfirmEmailToSubject, submitSubjectData } from 'store/subject'
import ThankYou from 'components/ThankYou'
import { toast } from 'react-toastify'
import moment from 'moment'
import Exhibitor from 'views/admin/subjects/HTMLForms/Exhibitor'
import { getStatusBasedSubTitle } from 'utils'
import Speaker from 'views/admin/subjects/HTMLForms/Speaker'
import Sponsor from 'views/admin/subjects/HTMLForms/Sponsor'
import useTokenValidation from 'hooks/useTokenValidation'
import TalksListing from 'views/admin/subjects/HTMLForms/TalksListing'
import LogoWithConference from 'components/LogoWithConference'

const SubjectSubmissionForm = () => {
  const classes = useStyles()
  const location = useLocation()
  const token = new URLSearchParams(location.search).get('token')
  const dispatch = useDispatch()

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const { subject } = useSelector((state) => state.subjects)
  const { conference } = useSelector((state) => state.conference)
  const { isLoading, isTokenValid } = useTokenValidation()

  const hideForm = () => {
    setShowForm(false)
  }

  useEffect(() => {
    if (
      (!isFormSubmitted && subject?.status === SubjectStatus.AWAITING_RESPONSE) ||
      (subject?.updateInfoNotificationSent && !subject.submittedAt)
    ) {
      setShowForm(true)
    }
  }, [dispatch, subject?.status])

  const handleEditSubmittedForm = async () => {
    setIsFormSubmitted(false)
    setShowForm(true)
    await dispatch(fetchSubjectByToken(token))
  }

  const handleSubmitSubject = async (values) => {
    const nowDateTime = moment().utc().second(0).milliseconds(0).valueOf()
    const payload = {
      websiteUrl: values.websiteUrl.trim(),
      status: SubjectStatus.PENDING_DATA_REVIEW,
      submittedAt: nowDateTime
    }
    if (subject?.type === SubjectType.EXHIBITOR) {
      payload.name = values.name.trim()
      payload.booth = values.booth.trim()
      payload.intro = values.intro.trim()
      payload.message = values.message.trim()
    } else if (subject?.type === SubjectType.SPONSOR) {
      payload.email = values.email.trim()
      payload.fullName = values.fullName.trim()
      payload.intro = values.intro.trim()
      payload.sponsorMessage = values.sponsorMessage.trim()
      payload.sponsorAdTargetURL = values.sponsorAdTargetURL.trim()
      payload.booth = values?.booth ? values?.booth.trim() : ''
    } else {
      payload.email = values.email.trim()
      payload.fullName = values.fullName.trim()
      payload.bio = values.bio.trim()
      payload.company = values.company.trim()
      payload.position = values.position.trim()
    }

    await dispatch(submitSubjectData(subject?.id, { ...subject, ...payload }))
    await sendConfirmEmailToSubject(subject?.id)
    setIsFormSubmitted(true)
    setShowForm(false)
    toast.success('Data submitted successfully.')
  }

  if (isLoading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )
  else if (!isTokenValid) return <PageNotFound />
  else if (isFormSubmitted && !showForm && subject?.type !== SubjectType.SPEAKER)
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle="Thank you for submitting the requested form. We appreciate your cooperation. If you want to make any changes, please click the button below."
      />
    )
  else if (
    (subject?.status === SubjectStatus.APPROVED ||
      subject?.status === SubjectStatus.PENDING_DATA_REVIEW) &&
    !showForm &&
    subject?.type !== SubjectType.SPEAKER
  )
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle={getStatusBasedSubTitle(subject?.status)}
      />
    )
  else if (
    !showForm &&
    subject?.type === SubjectType.SPEAKER &&
    (subject?.status === SubjectStatus.APPROVED ||
      subject?.status === SubjectStatus.PENDING_DATA_REVIEW)
  )
    return <TalksListing onAction={handleEditSubmittedForm} />
  else if (!isFormSubmitted && showForm && subject?.type === SubjectType.EXHIBITOR)
    return (
      <Container className={classes.container}>
        <LogoWithConference confName={conference?.confName} />
        <Exhibitor
          subject={subject}
          handleSubmit={handleSubmitSubject}
          submitTitle="Submit Your Information"
          reason={subjectReason.registration}
        />
      </Container>
    )
  else if (!isFormSubmitted && showForm && subject?.type === SubjectType.SPEAKER)
    return (
      <Container className={classes.container}>
        <LogoWithConference confName={conference?.confName} />
        <Speaker
          subject={subject}
          handleSubmit={handleSubmitSubject}
          submitTitle="Submit Your Information"
          hideForm={hideForm}
          reason={subjectReason.registration}
        />
      </Container>
    )
  else if (!isFormSubmitted && showForm && subject?.type === SubjectType.SPONSOR)
    return (
      <Container className={classes.container}>
        <LogoWithConference confName={conference?.confName} />
        <Sponsor
          subject={subject}
          showSponsorshipLevel={false}
          handleSubmit={handleSubmitSubject}
          submitTitle="Submit Your Information"
          reason={subjectReason.registration}
        />
      </Container>
    )
  else return null
}
export default SubjectSubmissionForm

const useStyles = makeStyles({
  container: {
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem'
  }
})
